/**
 * Chat message crypto compatible with the shared native Locs security implementation
 * (`locs-clang-security`): EncryptMsg/DecryptMsg using
 * AES-256-ECB + custom padding + base64.
 */

import aesjs from 'aes-js';

function writeUint32BE(buf: Uint8Array, offset: number, value: number) {
  const v = new DataView(buf.buffer, buf.byteOffset + offset, 4);
  v.setUint32(0, value >>> 0, false);
}

function readUint32BE(buf: Uint8Array, offset: number): number {
  const v = new DataView(buf.buffer, buf.byteOffset + offset, 4);
  return v.getUint32(0, false);
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/** 32-byte AES key (same as server AES_KEY). */
export function encryptMsg(plain: string, aesKeyUtf8: string): string {
  const keyBytes = aesjs.utils.utf8.toBytes(aesKeyUtf8);
  if (keyBytes.length !== 32) {
    throw new Error('AES_KEY must be 32 bytes');
  }
  const inData = new TextEncoder().encode(plain);
  const nInLen = inData.length;
  let nRemain = nInLen % 16;
  let nBlocks = Math.floor((nInLen + 15) / 16);
  if (nRemain > 12 || nRemain === 0) {
    nBlocks += 1;
  }
  const nEncryptLen = nBlocks * 16;
  const pData = new Uint8Array(nEncryptLen);
  pData.set(inData);
  writeUint32BE(pData, nEncryptLen - 4, nInLen);
  const aesEcb = new aesjs.ModeOfOperation.ecb(keyBytes);
  const enc = aesEcb.encrypt(pData);
  return uint8ToBase64(enc);
}

export function decryptMsg(b64Cipher: string, aesKeyUtf8: string): string {
  const keyBytes = aesjs.utils.utf8.toBytes(aesKeyUtf8);
  if (keyBytes.length !== 32) {
    throw new Error('AES_KEY must be 32 bytes');
  }
  const raw = base64ToUint8(b64Cipher);
  if (raw.length === 0 || raw.length % 16 !== 0) {
    throw new Error('invalid ciphertext');
  }
  const aesEcb = new aesjs.ModeOfOperation.ecb(keyBytes);
  const dec = aesEcb.decrypt(raw);
  const nInLen = readUint32BE(dec, dec.length - 4);
  if (nInLen > dec.length) {
    throw new Error('invalid length');
  }
  return new TextDecoder().decode(dec.subarray(0, nInLen));
}
